import React, { MouseEventHandler, useEffect, useState } from "react";
import "./styles.scss";
import { graphql, navigate, useStaticQuery } from "gatsby";
import cn from "classnames";
import { useFlexSearch } from "react-use-flexsearch";
import { TextButton } from "@trussle/external-components";
import { SearchResultCard } from "../../../SearchResultCard";
import { ISplashPanelProps } from "../OriginalSplashPanel";
import { SearchBar } from "./SearchBar";
import { ArticleCategory, CategoryToColourMap } from "../../../../types/common";
import { trackEvent } from "../../../../helpers/analytics";
import { PanelWrapper } from "../../../PanelWrapper";

interface NormalizedArticleModel {
  id: string;
  title: string;
  subtitle: string;
  url: string;
  urlText: string;
  imageTitle: string;
  imageUrl: string;
  gatsbyImageData: string;
  readingTime: string;
  category: ArticleCategory;
}

interface DenormalizedArticleModel {
  id: string;
  title: string;
  subtitle: string;
  readingTime: string;
  category: ArticleCategory;
  url: string;
  image: {
    file: {
      url: string;
    };
    title: string;
    gatsbyImageData: string;
  };
}

const RESULTS_SHOW_INCREMENT = 4;

const componentQuery = graphql`
  query {
    localSearchArticleCards {
      index
      store
    }
  }
`;

const SearchResultsText: React.FC<{ resultsCount: number, searchQuery: string }> = ({
  resultsCount,
  searchQuery,
}) => {
  if (!searchQuery) return null;

  if (resultsCount === 0) {
    return (
      <div className="articleSearchSplashPanel__noResultsContainer">
        <p>
          {`We couldn't find any articles about "${searchQuery}". Try adjusting your search terms.`}
        </p>
      </div>
    );
  }

  const noun = resultsCount === 1 ? "Result" : "Results";
  return (
    <h3 className="articleSearchSplashPanel__resultsCount">
      {`${resultsCount} ${noun} for '${searchQuery}'`}
    </h3>
  );
};

export const ArticleSearchSplashPanel: React.FC<ISplashPanelProps> = ({
  title, betterV2,
}) => {
  const data = useStaticQuery(componentQuery);
  const index = data?.localSearchArticleCards.index;
  const store = data?.localSearchArticleCards.store;

  if (!index || !store) {
    return null;
  }

  const [searchQuery, setSearchQuery] = useState("");
  const [showResults, setShowResults] = useState(RESULTS_SHOW_INCREMENT);
  const results = useFlexSearch(searchQuery, index, store);
  const slice = results.slice(0, showResults);
  const articles = unflatten(slice);

  useEffect(() => {
    if (searchQuery && results.length === 0) {
      trackEvent("No results for search term", {
        module: "ArticleSearchSplashPanel",
        element_type: "Input",
        section: "Search Bar",
        typed: searchQuery,
      });
    }
  }, [results, searchQuery]);

  const handleSetSearchQuery = (input: string) => {
    setSearchQuery(input);
    setShowResults(RESULTS_SHOW_INCREMENT);
    trackEvent("Search typed", {
      module: "ArticleSearchSplashPanel",
      element_type: "Input",
      section: "Search Bar",
      typed: input,
    });
  };

  const handleLoadMore = () => {
    setShowResults((prevShowResults) => prevShowResults + RESULTS_SHOW_INCREMENT);
    trackEvent("Load more", {
      module: "ArticleSearchSplashPanel",
      element_type: "Button",
      section: "Search results",
      typed: searchQuery,
    });
  };

  const onClickResult: MouseEventHandler<HTMLAnchorElement> = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    const url = event.currentTarget.href;
    await trackEvent("Result clicked", {
      module: "ArticleSearchSplashPanel",
      element_type: "Link",
      section: "Search results",
      url,
    });
    navigate(url);
  };
  return (
    <PanelWrapper backgroundColour={betterV2 ? "light-yellow" : undefined} isBetterV2={betterV2}>
      <div className={cn("articleSearchSplashPanel", { "articleSearchSplashPanel-betterV2": betterV2 })}>
        <div className="articleSearchSplashPanel__content">
          <h1 className="articleSearchSplashPanel__title">{title}</h1>
          <div className="articleSearchSplashPanel__searchContainer">
            <SearchBar
              searchQuery={searchQuery}
              setSearchQuery={handleSetSearchQuery}
              betterV2={betterV2}
            />
            <SearchResultsText resultsCount={results.length} searchQuery={searchQuery} />
          </div>
          <div className="articleSearchSplashPanel__searchResults">
            {articles.map((article) => (
              <SearchResultCard
                key={article.id}
                title={article.title}
                description={article.subtitle}
                image={article.image}
                categoryTagColour={CategoryToColourMap[article.category]}
                timeTag={article.readingTime}
                categoryTag={article.category}
                url={article.url}
                onClick={onClickResult}
              />
            ))}
          </div>
          {results.length > showResults && (
            <div className="articleSearchSplashPanel__loadMore">
              <TextButton value="Load more" onClick={handleLoadMore} />
            </div>
          )}
        </div>
      </div>
    </PanelWrapper>
  );
};

function unflatten(results: NormalizedArticleModel[]): DenormalizedArticleModel[] {
  return results.map((result) => ({
    id: result.id,
    title: result.title,
    subtitle: result.subtitle,
    readingTime: result.readingTime,
    category: result.category,
    url: result.url,
    image: {
      file: {
        url: result.imageUrl,
      },
      title: result.imageTitle,
      gatsbyImageData: result.gatsbyImageData,
    },
  }));
}
